import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, Input } from 'semantic-ui-react';
import CustomModal from '../../configuracion/components/customModal';
import PlanDeCuentaDropdown, { PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX } from '../../../containers/PlanDeCuentaDropdown/PlanDeCuentaDropdown';
import RuleCostCenterAllocationsEditor, {
  costCenterAllocationsFromRule,
  normalizeCostCenterAllocationsForApi,
} from './RuleCostCenterAllocationsEditor';
import AddDashedOutlineButton from '../../general/js/forms/reconcile/contabilidad/AddDashedOutlineButton';
import { documentsDic } from '../../../utils/dataDictionary';
import { formatNewMoney } from '../../../utils/formatter';
import {
  flattenExceptionsForEdit,
  getImpossibleAmountAndJoins,
  getImpossibleExclusiveValueAndJoins,
  isValidExceptionAmount,
  parseExceptionAmountInput,
  resolveExceptionJoinOperator,
} from '../exceptionFilters.helpers';
import './CreateContactRuleModal.scss';

const CreateContactRuleModal = ({
  open,
  onClose,
  onSave,
  agenda,
  category, // 'client' | 'provider'
  editingRule,
  isSaving = false,
  isGlobalRule = false,
  companyId,
  initialFormValues = null,
  documentSubType = 'DTE', // 'DTE' | 'HONORARIO'
}) => {
  const { t } = useTranslation('configuracion');
  const [formData, setFormData] = useState({
    description: '',
    accountKey: '',
    accountName: '',
    costCenterAllocations: [],
    documentExceptions: [],
  });

  const [errors, setErrors] = useState({});
  const [showExceptionValidationHints, setShowExceptionValidationHints] = useState(false);

  useEffect(() => {
    if (editingRule) {
      const flattened = flattenExceptionsForEdit(editingRule.documentExceptions);
      const firstStored = editingRule.documentExceptions?.[0];
      setFormData({
        description: editingRule.description || '',
        accountKey: editingRule.accountKey || '',
        accountName: editingRule.accountName || '',
        costCenterAllocations: costCenterAllocationsFromRule(editingRule),
        documentExceptions: flattened.length > 0
          ? [{
              ...flattened[0],
              accountKey: firstStored?.accountKey || '',
              accountName: firstStored?.accountName || '',
            }]
          : [],
      });
    } else {
      setFormData({
        description: initialFormValues?.description || '',
        accountKey: initialFormValues?.accountKey || '',
        accountName: initialFormValues?.accountName || '',
        costCenterAllocations: [],
        documentExceptions: [],
      });
    }
    setErrors({});
    setShowExceptionValidationHints(false);
  }, [editingRule, open, initialFormValues]);

  const exceptionFilterKindOptions = useMemo(() => [
    { key: 'AMOUNT', value: 'AMOUNT', text: t('categorization.contactRuleModal.exceptionKindAmount') },
    { key: 'DOCUMENT_CODE', value: 'DOCUMENT_CODE', text: t('categorization.contactRuleModal.exceptionKindDocumentCode') },
  ], [t]);

  const exceptionAmountOperatorOptions = useMemo(() => [
    { key: 'GT', value: 'GT', text: t('categorization.contactRuleModal.exceptionAmountGt') },
    { key: 'LT', value: 'LT', text: t('categorization.contactRuleModal.exceptionAmountLt') },
    { key: 'EQ', value: 'EQ', text: t('categorization.contactRuleModal.exceptionAmountEq') },
  ], [t]);

  const exceptionJoinOperatorOptions = useMemo(() => [
    { key: 'AND', value: 'AND', text: t('categorization.contactRuleModal.exceptionJoinAnd') },
    { key: 'OR', value: 'OR', text: t('categorization.contactRuleModal.exceptionJoinOr') },
  ], [t]);

  const documentCodeOptions = useMemo(() => {
    const isHonorario = documentSubType === 'HONORARIO';
    return Object.entries(documentsDic)
      .filter(([code, meta]) => {
        if (!meta?.isDocumentCodeInUse) return false;
        if (!/^\d+$/.test(String(code))) return false;
        if (isHonorario) return code === '0' || code === '1';
        return code !== '0' && code !== '1';
      })
      .map(([code, meta]) => ({
        key: code,
        value: code,
        text: `${code} - ${meta.fullName || meta.label || meta.name}`,
      }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [documentSubType]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleAccountChange = (e, data) => {
    setFormData((prev) => ({
      ...prev,
      accountKey: data?.value || '',
      accountName: data?.label || '',
    }));
    if (errors.accountKey) {
      setErrors((prev) => ({ ...prev, accountKey: null }));
    }
  };

  const getExceptionFilters = () => formData.documentExceptions?.[0]?.filters || [];

  const isCompleteExceptionFilter = (filter) => {
    if (!filter?.kind) return false;
    if (filter.kind === 'DOCUMENT_CODE') return Boolean(String(filter.value || '').trim());
    if (filter.kind === 'AMOUNT') {
      const amount = Number(filter.amount);
      return Boolean(filter.operator) && isValidExceptionAmount(amount);
    }
    return false;
  };

  const getImpossibleAndJoins = (filters = getExceptionFilters()) => [
    ...getImpossibleAmountAndJoins(filters, { isCompleteFilter: isCompleteExceptionFilter }),
    ...getImpossibleExclusiveValueAndJoins(filters, {
      isCompleteFilter: isCompleteExceptionFilter,
      kind: 'DOCUMENT_CODE',
    }),
  ];

  const getImpossibleWarningCopy = (issues) => {
    if (!issues?.length) return null;
    if (issues.some((i) => i.kind === 'document_code')) {
      return {
        hint: t('categorization.contactRuleModal.exceptionImpossibleDocumentCodeHint'),
        saveError: t('categorization.contactRuleModal.exceptionImpossibleDocumentCode'),
      };
    }
    if (issues.some((i) => i.kind === 'prefer_eq')) {
      return {
        hint: t('categorization.contactRuleModal.exceptionImpossibleAmountPreferEqHint'),
        saveError: t('categorization.contactRuleModal.exceptionImpossibleAmountPreferEq'),
      };
    }
    if (issues.every((i) => i.kind === 'prefer_or')) {
      return {
        hint: t('categorization.contactRuleModal.exceptionImpossibleAmountPreferOrHint'),
        saveError: t('categorization.contactRuleModal.exceptionImpossibleAmountPreferOr'),
      };
    }
    return {
      hint: t('categorization.contactRuleModal.exceptionImpossibleAmountGenericHint'),
      saveError: t('categorization.contactRuleModal.exceptionImpossibleAmountGeneric'),
    };
  };

  const validateExceptionsBeforeSave = () => {
    const filters = getExceptionFilters();
    if (filters.length === 0) return { ok: true };
    const hasIncomplete = filters.some((filter) => !isCompleteExceptionFilter(filter));
    if (hasIncomplete) {
      return {
        ok: false,
        message: t('categorization.contactRuleModal.exceptionIncompleteFilter'),
      };
    }
    const exceptionAccountKey = String(formData.documentExceptions?.[0]?.accountKey || '').trim();
    if (!exceptionAccountKey) {
      return {
        ok: false,
        message: t('categorization.contactRuleModal.exceptionAccountRequired'),
      };
    }
    const impossibleJoins = getImpossibleAndJoins(filters);
    if (impossibleJoins.length > 0) {
      const warningCopy = getImpossibleWarningCopy(impossibleJoins);
      return {
        ok: false,
        message: warningCopy?.saveError || t('categorization.contactRuleModal.exceptionImpossibleAmountGeneric'),
      };
    }
    return { ok: true };
  };

  const normalizeExceptionsForSubmit = (exceptions) => {
    if (!Array.isArray(exceptions)) return [];
    const firstRow = exceptions.find((row) => Array.isArray(row?.filters) && row.filters.length > 0);
    if (!firstRow) return [];
    const accountKey = String(firstRow.accountKey || '').trim();
    const accountName = String(firstRow.accountName || '').trim();
    if (!accountKey || !accountName) return [];
    const filters = (firstRow.filters || [])
      .map((filter, filterIndex) => {
        const joinOperator = resolveExceptionJoinOperator(filterIndex, filter.joinOperator);
        if (filter.kind === 'DOCUMENT_CODE') {
          const value = String(filter.value || '').trim();
          if (!value) return null;
          return { kind: 'DOCUMENT_CODE', value, ...(joinOperator ? { joinOperator } : {}) };
        }
        if (filter.kind === 'AMOUNT') {
          const amount = Number(filter.amount);
          const operator = filter.operator;
          if (!operator || !isValidExceptionAmount(amount)) return null;
          return {
            kind: 'AMOUNT',
            operator,
            amount,
            ...(joinOperator ? { joinOperator } : {}),
          };
        }
        return null;
      })
      .filter(Boolean);
    return filters.length > 0 ? [{ filters, accountKey, accountName }] : [];
  };

  const addExceptionFilter = () => {
    setFormData((prev) => {
      const existing = prev.documentExceptions?.[0];
      if (!existing) {
        return {
          ...prev,
          documentExceptions: [{
            filters: [{ kind: 'AMOUNT', operator: 'GT', amount: undefined }],
            accountKey: '',
            accountName: '',
          }],
        };
      }
      return {
        ...prev,
        documentExceptions: [{
          ...existing,
          filters: [
            ...(existing.filters || []),
            { kind: 'AMOUNT', operator: 'GT', amount: undefined, joinOperator: 'AND' },
          ],
        }],
      };
    });
  };

  const updateExceptionFilter = (filterIndex, patch) => {
    setFormData((prev) => {
      const existing = prev.documentExceptions?.[0] || { filters: [], accountKey: '', accountName: '' };
      const filters = [...(existing.filters || [])];
      filters[filterIndex] = { ...(filters[filterIndex] || {}), ...patch };
      return { ...prev, documentExceptions: [{ ...existing, filters }] };
    });
  };

  const handleExceptionAccountChange = (e, data) => {
    setFormData((prev) => {
      const existing = prev.documentExceptions?.[0] || { filters: [] };
      return {
        ...prev,
        documentExceptions: [{
          ...existing,
          accountKey: data?.value || '',
          accountName: data?.label || '',
        }],
      };
    });
    if (errors.documentExceptions) {
      setErrors((prev) => ({ ...prev, documentExceptions: null }));
    }
  };

  const removeExceptionFilter = (filterIndex) => {
    setFormData((prev) => {
      const existing = prev.documentExceptions?.[0];
      if (!existing) return prev;
      const filters = (existing.filters || []).filter((_, i) => i !== filterIndex);
      if (filters.length === 0) {
        return { ...prev, documentExceptions: [] };
      }
      const normalized = filters.map((filter, index) => (
        index === 0 ? { ...filter, joinOperator: undefined } : filter
      ));
      return { ...prev, documentExceptions: [{ ...existing, filters: normalized }] };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.accountKey) newErrors.accountKey = t('categorization.contactRuleModal.accountRequired');
    const rows = formData.costCenterAllocations || [];
    const filled = rows.filter((r) => r.costCenterId || r.percent !== '');
    if (filled.length) {
      const incomplete = filled.some((r) => !r.costCenterId || r.percent === '');
      if (incomplete) {
        newErrors.costCenterAllocations = 'Completa centro de costo y porcentaje en cada fila';
      } else {
        const sum = filled.reduce((s, r) => s + (Number(r.percent) || 0), 0);
        if (Math.abs(sum - 100) > 0.01) {
          newErrors.costCenterAllocations = 'Los porcentajes deben sumar 100%';
        }
        const ids = filled.map((r) => r.costCenterId);
        if (new Set(ids).size !== ids.length) {
          newErrors.costCenterAllocations = 'No repitas el mismo centro de costo';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    setShowExceptionValidationHints(true);
    if (!validate()) return;
    const exceptionsValidation = validateExceptionsBeforeSave();
    if (!exceptionsValidation.ok) {
      setErrors((prev) => ({ ...prev, documentExceptions: exceptionsValidation.message }));
      return;
    }
    onSave({
      ...formData,
      costCenterAllocations: normalizeCostCenterAllocationsForApi(formData.costCenterAllocations),
      documentExceptions: normalizeExceptionsForSubmit(formData.documentExceptions),
    });
  };

  const handleClose = () => {
    setFormData({
      description: '',
      accountKey: '',
      accountName: '',
      costCenterAllocations: [{ costCenterId: '', percent: '' }],
      documentExceptions: [],
    });
    setErrors({});
    setShowExceptionValidationHints(false);
    onClose();
  };

  const title = useMemo(() => {
    if (isGlobalRule) {
      return category === 'provider'
        ? t('categorization.contactRuleModal.globalProviderTitle')
        : t('categorization.contactRuleModal.globalClientTitle');
    }
    if (category === 'provider') {
      return editingRule
        ? t('categorization.contactRuleModal.editProviderTitle')
        : t('categorization.contactRuleModal.createProviderTitle');
    }
    return editingRule
      ? t('categorization.contactRuleModal.editClientTitle')
      : t('categorization.contactRuleModal.createClientTitle');
  }, [category, editingRule, isGlobalRule, t]);

  const confirmLabel = useMemo(() => {
    if (isGlobalRule) return t('categorization.contactRuleModal.confirmGlobal');
    if (editingRule) return t('common.saveChanges');
    return t('categorization.contactRuleModal.confirmCreate');
  }, [editingRule, isGlobalRule, t]);

  const filters = getExceptionFilters();
  const impossibleJoins = getImpossibleAndJoins(filters);
  const impossibleJoinIndexes = new Set(impossibleJoins.map((i) => i.filterIndex));
  const warningCopy = getImpossibleWarningCopy(impossibleJoins);
  const incompleteIndexes = new Set(
    filters
      .map((filter, index) => (isCompleteExceptionFilter(filter) ? -1 : index))
      .filter((index) => index >= 0),
  );
  const showIncompleteHints = showExceptionValidationHints && incompleteIndexes.size > 0;
  const showImpossibleHints = showExceptionValidationHints && Boolean(warningCopy);

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      title={title}
      classNameContent="create-contact-rule-modal-content-wrapper"
      content={
        <div className="create-contact-rule-modal-content">
          <div className="create-contact-rule-summary">
            {isGlobalRule ? (
              <>
                <div className="create-contact-rule-summary-title">
                  {t('categorization.contactRuleModal.globalRule')}
                </div>
                <div className="create-contact-rule-summary-sub">
                  {category === 'provider'
                    ? t('categorization.contactRuleModal.globalProviderHint')
                    : t('categorization.contactRuleModal.globalClientHint')}
                </div>
              </>
            ) : (
              <>
                <div className="create-contact-rule-summary-title">
                  {agenda?.name || t('categorization.contactRuleModal.contactFallback')}
                </div>
                <div className="create-contact-rule-summary-sub">
                  {agenda?.rut || ''}
                </div>
              </>
            )}
          </div>

          <div className="create-contact-rule-field">
            <label className="create-contact-rule-label">
              {t('categorization.contactRuleModal.accountLabel')}{' '}
              <span className="create-contact-rule-required">*</span>
            </label>
            <PlanDeCuentaDropdown
              value={formData.accountKey}
              onChange={handleAccountChange}
              placeholder={t('categorization.contactRuleModal.accountPlaceholder')}
              className={errors.accountKey ? 'error' : ''}
              portalMenu
              menuZIndex={PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.MODAL}
            />
            {errors.accountKey && (
              <div className="create-contact-rule-error">{errors.accountKey}</div>
            )}
            <div className="create-contact-rule-hint">
              {t('categorization.contactRuleModal.accountHint')}
            </div>
          </div>

          <div className="create-contact-rule-field">
            <label className="create-contact-rule-label" htmlFor="contact-rule-description">
              {t('categorization.contactRuleModal.descriptionLabel')}
            </label>
            <input
              id="contact-rule-description"
              type="text"
              className="create-contact-rule-input"
              placeholder={t('categorization.contactRuleModal.descriptionExample')}
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </div>

          {companyId && (
            <RuleCostCenterAllocationsEditor
              companyId={companyId}
              modalOpen={open}
              value={formData.costCenterAllocations}
              onChange={(costCenterAllocations) =>
                setFormData((prev) => ({ ...prev, costCenterAllocations }))
              }
              error={errors.costCenterAllocations}
            />
          )}

          <div className="contact-rule-exceptions-section">
            <label className="create-contact-rule-label">
              {t('categorization.contactRuleModal.exceptionsSectionTitle')}
            </label>
            <div className="create-contact-rule-hint create-contact-rule-hint--block">
              {t('categorization.contactRuleModal.exceptionsSectionDesc')}
            </div>

            {filters.length > 0 && (
              <div className="contact-rule-exception-card">
                <div className="contact-rule-exception-card-header">
                  <Icon name="exchange" className="contact-rule-exception-icon" />
                  <span className="contact-rule-exception-card-title">
                    {t('categorization.contactRuleModal.exceptionsBlockTitle')}
                  </span>
                </div>
                {showIncompleteHints && (
                  <div className="contact-rule-exception-warning" role="alert">
                    <Icon name="warning sign" />
                    <span>{t('categorization.contactRuleModal.exceptionIncompleteFilterHint')}</span>
                  </div>
                )}
                {showImpossibleHints && (
                  <div className="contact-rule-exception-warning" role="alert">
                    <Icon name="warning sign" />
                    <span>{warningCopy.hint}</span>
                  </div>
                )}
                {errors.documentExceptions && (
                  <div className="create-contact-rule-error">{errors.documentExceptions}</div>
                )}
                <div className="contact-rule-exception-filters">
                  {filters.map((filter, filterIndex) => (
                    <React.Fragment key={`contact-exception-filter-${filterIndex}`}>
                      {filterIndex > 0 && (
                        <div
                          className={`contact-rule-exception-join-row${
                            showImpossibleHints && impossibleJoinIndexes.has(filterIndex) ? ' is-impossible' : ''
                          }`}
                        >
                          <span className="contact-rule-exception-join-connector" aria-hidden="true" />
                          <div
                            className="contact-rule-exception-join-toggle"
                            role="group"
                            aria-label={t('categorization.contactRuleModal.exceptionJoinLabel')}
                          >
                            {exceptionJoinOperatorOptions.map((option) => {
                              const active = (filter.joinOperator || 'AND') === option.value;
                              return (
                                <button
                                  key={option.key}
                                  type="button"
                                  className={`contact-rule-exception-join-option${active ? ' is-active' : ''}`}
                                  aria-pressed={active}
                                  onClick={() => updateExceptionFilter(filterIndex, { joinOperator: option.value })}
                                >
                                  {option.text}
                                </button>
                              );
                            })}
                          </div>
                          <span className="contact-rule-exception-join-connector" aria-hidden="true" />
                        </div>
                      )}
                      <div
                        className={`contact-rule-exception-filter-row${
                          showIncompleteHints && incompleteIndexes.has(filterIndex) ? ' is-incomplete' : ''
                        }`}
                      >
                        <Dropdown
                          selection
                          options={exceptionFilterKindOptions}
                          value={filter.kind || 'AMOUNT'}
                          onChange={(_, { value }) => {
                            const patch = { kind: value };
                            if (value === 'AMOUNT') {
                              patch.operator = 'GT';
                              patch.amount = undefined;
                              patch.value = undefined;
                            } else {
                              patch.value = '';
                              patch.amount = undefined;
                              patch.operator = undefined;
                            }
                            updateExceptionFilter(filterIndex, patch);
                          }}
                          className="contact-rule-exception-kind"
                        />
                        {filter.kind === 'AMOUNT' && (
                          <>
                            <Dropdown
                              selection
                              options={exceptionAmountOperatorOptions}
                              value={filter.operator || 'GT'}
                              onChange={(_, { value }) => updateExceptionFilter(filterIndex, { operator: value })}
                              className="contact-rule-exception-operator"
                            />
                            <Input
                              type="text"
                              value={
                                filter.amount !== undefined && filter.amount !== null && filter.amount !== ''
                                  ? formatNewMoney(filter.amount)
                                  : ''
                              }
                              onChange={(e) => {
                                const num = parseExceptionAmountInput(e.target.value);
                                updateExceptionFilter(filterIndex, { amount: num });
                              }}
                              placeholder={t('categorization.contactRuleModal.exceptionAmountPlaceholder')}
                              fluid
                              className="contact-rule-exception-amount"
                            />
                          </>
                        )}
                        {filter.kind === 'DOCUMENT_CODE' && (
                          <Dropdown
                            selection
                            search
                            options={documentCodeOptions}
                            value={filter.value || ''}
                            onChange={(_, { value }) => updateExceptionFilter(filterIndex, { value: String(value) })}
                            placeholder={t('categorization.contactRuleModal.exceptionDocumentCodePlaceholder')}
                            fluid
                            className="contact-rule-exception-document-code"
                          />
                        )}
                        <button
                          type="button"
                          className="contact-rule-exception-remove-btn"
                          onClick={() => removeExceptionFilter(filterIndex)}
                          title={t('categorization.contactRuleModal.removeExceptionFilter')}
                          aria-label={t('categorization.contactRuleModal.removeExceptionFilter')}
                        >
                          <Icon name="close" />
                        </button>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className="contact-rule-exception-account">
                  <label className="contact-rule-exception-account-label" htmlFor="contact-exception-account">
                    {t('categorization.contactRuleModal.exceptionAccountLabel')}
                  </label>
                  <PlanDeCuentaDropdown
                    id="contact-exception-account"
                    value={formData.documentExceptions?.[0]?.accountKey || ''}
                    onChange={handleExceptionAccountChange}
                    placeholder={t('categorization.contactRuleModal.exceptionAccountPlaceholder')}
                    portalMenu
                    menuZIndex={PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.MODAL}
                  />
                </div>
              </div>
            )}

            <AddDashedOutlineButton
              onClick={addExceptionFilter}
              className="contact-rule-exception-add-btn"
            >
              {t('categorization.contactRuleModal.addExceptionFilter')}
            </AddDashedOutlineButton>
          </div>
        </div>
      }
      confirmLabel={confirmLabel}
      onConfirm={handleSave}
      confirmDisabled={isSaving}
      isLoading={isSaving}
      cancelarLabel={t('common.cancel')}
      size="tiny"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        width: 'min(720px, 95vw)',
        maxWidth: '95vw',
        minWidth: 'min(520px, 95vw)',
        maxHeight: '90vh',
        overflow: 'visible',
        margin: '0 auto',
      }}
    />
  );
};

export default CreateContactRuleModal;
